import React, { useMemo } from 'react';
import { Box, Divider, List, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { withStyles } from 'tss-react/mui';
import { useAssessmentsKeysQuery, useAssessmentSubmissionsQuery, useFormDefinitionQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { calculateAssessmentPercentChange } from '@uplift/utils';
import { Modal } from '../../../Modal';
import { Loader } from '../../../Loader';
import { NavigationBackButton, ProviderDialogNavigation, ReviewLayout, ReviewLayoutContentControlsProvider } from '../../../Layout';
import { SmallProgressChart } from '../../../Charts';
import { PercentageDiffCircle } from '../../../UserActivityItem';
import { AssessmentSubmissionItem } from './AssessmentSubmissionListItem.component';
import { AssessmentView } from './AssessmentView.component';
import { AssessmentKeys, getAssessmentFormMaxScore } from '@uplift/types';
import { AssessmentReviewContainer } from './AssessmentReviewContainer.component';
import { useL10n } from '@uplift/hooks';
import { useHistory } from 'react-router';
import { providerPatientRoute } from '@uplift/constants';

const StyledPercentageDiffCircle = withStyles(PercentageDiffCircle, {
  root: {
    lineHeight: '48px',
    width: '48px',
    height: '48px',
  }
});

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: '100vh',
  },
  score: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  background: {
    backgroundColor: theme.palette.grey[50],
  },
  chart: {
    backgroundColor: theme.palette.common.white,
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.palette.divider}`,

    maxWidth: '1118px',
    width: '100%',
  },
  reviewSection: {
    display: 'flex',
    flex: 1,
    backgroundColor: theme.palette.grey[50],
  },
  review: {
    maxWidth: '730px',
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(5, 10),
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  reviewLayoutSection: {
    height: 'calc(100vh - 64px)',
  }
}));

export type AssessmentSubmissionsReviewProps = {
  onClose: () => void;
  patientId: string;
  assessmentId?: string;
  assessmentKey?: AssessmentKeys;
  now?: DateObject;
};

const AssessmentSubmissionsReview: React.FC<AssessmentSubmissionsReviewProps> = (props) => {
  const history = useHistory();
  const now = React.useMemo(() => props.now ?? CalendarDateService.date(), [ props.now ]);
  const { classes } = useStyles();
  const l10n = useL10n();
  const { data: formDefinition } = useFormDefinitionQuery(props.assessmentId, { suspense: true });
  const { data: submissions } = useAssessmentSubmissionsQuery(props.patientId, props.assessmentKey, { suspense: true });
  const { data: assessmentKeys } = useAssessmentsKeysQuery({ suspense: true });
  const { data: patientName } = useUserDisplayNameQuery(props.patientId, { enabled: !!props?.patientId });
  const [ _selectedSubmissionId, setSelectedSubmissionId ] = React.useState<string>();
  const completedSubmissions = React.useMemo(() => submissions?.filter(s => s.submission.completedAt) ?? [], [ submissions ]);
  const newestSubmission = completedSubmissions?.[0];
  const baselineSubmission = completedSubmissions?.[completedSubmissions?.length - 1];
  const selectedSubmissionId = _selectedSubmissionId || newestSubmission?.submission?.formSubmissionId;
  const selectedSubmission = React.useMemo(() => submissions?.find(s => s.submission.formSubmissionId === selectedSubmissionId), [ submissions, selectedSubmissionId ]);
  const isBaselineSelected = selectedSubmission?.submission?.formSubmissionId === baselineSubmission?.submission?.formSubmissionId;

  const maxScore = useMemo(() => getAssessmentFormMaxScore(formDefinition?.formId, assessmentKeys), [ assessmentKeys, formDefinition?.formId ]);

  const chartData = submissions?.map(submission => ({
    datetime: CalendarDateService.date(submission.submission.createdAt),
    score: submission.score,
    scoreDiffPercentage: submission.scoreDiffPercentage,
  })) ?? [];

  const handleClose = () => {
    history.push(providerPatientRoute.createLink({ patientId: props.patientId }));
    props.onClose();
  };

  return (
    <Box className={classes.root}>
      <ProviderDialogNavigation
        position="sticky"
        title={`${formDefinition?.name} History`}
        leftActions={(
          <NavigationBackButton onClick={handleClose}>
            {l10n.clientProfile.assessments.navButtonBack(patientName?.first ?? '')}
          </NavigationBackButton>
        )}
      />
      <Box className={classes.background}>

        <Box className={classes.chartContainer}>
          {completedSubmissions.length > 1 && (
            <Box className={classes.chart}>
              <Box px={15} py={5}>
                {newestSubmission && (
                  <Box className={classes.score}>
                    <Typography component="span" variant="h2" style={{ fontSize: '48px' }}>
                      {newestSubmission?.score}
                    </Typography>
                    {baselineSubmission && (
                      <StyledPercentageDiffCircle
                        percentage={calculateAssessmentPercentChange(newestSubmission.score, baselineSubmission.score)}
                      />
                    )}
                  </Box>
                )}
                <Box mt={5}>
                  <SmallProgressChart
                    aspectRatio={6 / 1}
                    data={chartData}
                    fillUpTo={now}
                    maxScore={maxScore}
                  />
                </Box>
              </Box>
              <Divider />
            </Box>
          )}
          <ReviewLayoutContentControlsProvider
            items={completedSubmissions}
            findSelectedIndex={(item) => selectedSubmission?.submission.formSubmissionId === item.submission.formSubmissionId}
            selectedItem={selectedSubmission}
            onSelectItem={(item) => setSelectedSubmissionId(item.submission.formSubmissionId)}
          >
            <ReviewLayout
              sidebarWidth="300px"
              minSidebarWidth="300px"
              classes={{ reviewSection: classes.reviewLayoutSection }}
              sidebar={(
                <List disablePadding>
                  {submissions?.map((submission) => (
                    <AssessmentSubmissionItem
                      key={submission.submission.formSubmissionId}
                      selected={submission.submission.formSubmissionId === selectedSubmissionId}
                      onSelect={() => setSelectedSubmissionId(submission.submission.formSubmissionId)}
                      score={submission.score}
                      submission={submission.submission}
                    />
                  ))}
                </List>
              )}
              content={selectedSubmission && props.assessmentKey && (
                <AssessmentReviewContainer title={formDefinition?.name ?? ''} sentBy="Uplift">
                  <AssessmentView
                    submission={selectedSubmission.submission}
                    assessmentKey={props.assessmentKey}
                    score={selectedSubmission.score}
                    percentage={baselineSubmission && !isBaselineSelected
                      ? calculateAssessmentPercentChange(selectedSubmission.score, baselineSubmission.score)
                      : undefined
                    }
                  />
                </AssessmentReviewContainer>
              )}
            />
          </ReviewLayoutContentControlsProvider>
        </Box>
      </Box>
    </Box>
  );
};


export type AssessmentSubmissionsReviewModalProps = {
  open: boolean;
  onClose: () => void;
} & AssessmentSubmissionsReviewProps;


export const AssessmentSubmissionsReviewModal: React.FC<AssessmentSubmissionsReviewModalProps> = (props) => (
  <Modal
    open={props.open}
    onClose={props.onClose}
    fullScreen
  >
    <React.Suspense fallback={<Loader />}>
      <AssessmentSubmissionsReview {...props} />
    </React.Suspense>
  </Modal>
);
